<template>
  <standard-layout :title="league?.name || 'League'">
    <template v-slot:append-extra>
      <v-btn icon @click="$dialog.push('qrcode')">
        <v-icon>mdi-qrcode</v-icon>
      </v-btn>
    </template>

    <v-container fluid>
      <template v-if="!hasMemberName">
        <div class="d-flex align-center justify-center" style="min-height: calc(100vh - var(--v-layout-top) - 48px);">
          <player-name-card v-model="localMemberName" @submit="$store.user.immediateUpdate({ playerName: localMemberName })" />
        </div>
      </template>
      <template v-else>
        <template v-if="league">
          <div class="tournament-tabs">
            <v-tabs color="primary" v-model="tab" :grow="$vuetify.display.mobile">
              <v-tab value="teams">TEAMS</v-tab>
              <v-tab value="matches">MATCHES</v-tab>
              <v-tab value="schedule">SCHEDULE</v-tab>
              <v-tab v-if="isCreator" value="settings">SETTINGS</v-tab>
            </v-tabs>
          </div>

          <v-tabs-window v-model="tab">
            <v-tabs-window-item value="teams">
              <v-card flat style="max-width: 720px;">
                <v-card-text style="padding-bottom: 125px;">
                  <template v-if="league.squads.length > 0">
                    <template v-if="league.bracketSize >= 2">
                      <div class="d-flex align-center text-body-2 font-weight-medium text-medium-emphasis mb-2 mt-4">
                        <span>Final standings</span>
                        <v-tooltip v-if="showPayout && leaguePayoutInfo" open-on-click location="bottom">
                          <template v-slot:activator="{ props }">
                            <span v-bind="props" class="final-standings-dollar"><span class="final-standings-dollar-symbol">$</span></span>
                          </template>
                          <div class="payout-tooltip">
                            <template v-for="(row, i) in payoutTooltipRows" :key="i">
                              <v-divider v-if="i === payoutTooltipRows.length - 1" class="my-1" />
                              <div class="d-flex justify-space-between">
                                <span class="mr-4" :class="{ 'font-weight-medium': row.bold }">{{ row.label }}</span>
                                <span :class="{ 'font-weight-medium': row.bold }">{{ row.value }}</span>
                              </div>
                            </template>
                          </div>
                        </v-tooltip>
                      </div>
                      <v-list density="compact" class="final-standings-list">
                        <v-list-item v-for="item in finalStandingsList" :key="item.squad.id">
                          <template v-slot:prepend>
                            <span class="final-standings-medal-box">
                              <v-icon v-if="placeMedal(item)"
                                      :color="placeColors[item.place]"
                                      size="x-large">mdi-trophy-variant-outline</v-icon>
                              <span v-else>{{ item.position }}</span>
                            </span>
                          </template>
                          <v-list-item-title :class="{ 'text-disabled': !item.revealed }">{{ item.revealed ? item.squad.title : "TBD" }}</v-list-item-title>
                          <v-list-item-subtitle v-if="item.revealed && item.squad.membersSortedByStartOfLeagueOrdinal.length > 0">
                            {{ item.squad.membersSortedByStartOfLeagueOrdinal.map(m => m.name).join(", ") }}
                          </v-list-item-subtitle>
                          <template v-slot:append>
                            <span v-if="showPayout && item.payout != null" class="text-body-1">
                              ${{ item.payout }}
                            </span>
                          </template>
                        </v-list-item>
                      </v-list>
                    </template>

                    <p class="text-body-2 font-weight-medium text-medium-emphasis mb-2 mt-9">Round robin</p>
                    <v-data-table
                      class="mt-2 teams-table"
                      :headers="squadHeaders"
                      :items="rankedSquadsWithPayout"
                      item-value="squad.id"
                      density="compact"
                      items-per-page="-1"
                      hide-default-footer
                    >
                      <template #[`item.place`]="{ item }">
                        <v-icon v-if="league.finished && league.bracketSize < 2 && item.place !== null && item.place <= 3"
                                :color="placeColors[item.place]"
                                size="x-large"
                                class="ml-n2">mdi-trophy-variant-outline</v-icon>
                        <span v-else :class="{ 'text-medium-emphasis': item.place === null }">
                          {{ item.place ?? '-' }}
                        </span>
                      </template>
                      <template #[`item.team`]="{ item }">
                        <div class="d-flex align-center text-body-1">
                          <div class="d-flex align-center" style="min-width: 0;">
                            <v-tooltip
                              location="right"
                              open-on-click
                              :disabled="item.squad.membersSortedByStartOfLeagueOrdinal.length === 0"
                            >
                              <template v-slot:activator="{ props }">
                                  <span v-bind="props" class="d-flex align-center">
                                  <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 168px;">
                                    {{ item.squad.title }}
                                  </span>
                                  <v-icon
                                    v-if="item.squad.membersSortedByStartOfLeagueOrdinal.length > 0"
                                    icon="mdi-account-group"
                                    color="info"
                                    size="small"
                                    class="ml-1"
                                  />
                                </span>
                              </template>
                              <div style="text-align: left;">
                                <div v-for="member in item.squad.membersSortedByStartOfLeagueOrdinal" :key="member.id">
                                  {{ member.name }} ({{ member.startOfLeagueDisplayRating }})
                                </div>
                              </div>
                            </v-tooltip>
                          </div>
                        </div>
                      </template>
                      <template #[`item.record`]="{ item }">
                        <span :class="{ 'text-medium-emphasis': item.place === null }">
                          {{ item.wins }}-{{ item.losses }}
                        </span>
                      </template>
                      <template #[`item.points`]="{ item }">
                        <span :class="{ 'text-medium-emphasis': item.place === null }">
                          {{ item.points }}
                        </span>
                      </template>
                      <template #[`header.points`]>
                        <v-tooltip open-on-click location="top">
                          <template v-slot:activator="{ props }">
                            <span v-bind="props" class="text-info" style="cursor: pointer; border-bottom: 1px solid rgb(var(--v-theme-info));">Pts</span>
                          </template>
                          <span>{{ pointsTooltip }}</span>
                        </v-tooltip>
                      </template>
                      <template #[`item.goals`]="{ item }">
                        <span :class="{ 'text-medium-emphasis': item.place === null }">
                          {{ item.goalsFor }}-{{ item.goalsAgainst }}
                        </span>
                      </template>
                      <template v-if="showPayout" #[`item.payout`]="{ item }">
                        <span :class="{ 'text-medium-emphasis': item.place === null }">
                          {{ item.payout ?? '-' }}
                        </span>
                      </template>
                      <template #[`header.payout`]>
                        <v-tooltip open-on-click location="top">
                          <template v-slot:activator="{ props }">
                            <span v-bind="props" class="text-info" style="cursor: pointer; border-bottom: 1px solid rgb(var(--v-theme-info));">$</span>
                          </template>
                          <div class="payout-tooltip">
                            <template v-for="(row, i) in payoutTooltipRows" :key="i">
                              <v-divider v-if="i === payoutTooltipRows.length - 1" class="my-1" />
                              <div class="d-flex justify-space-between">
                                <span class="mr-4" :class="{ 'font-weight-medium': row.bold }">{{ row.label }}</span>
                                <span :class="{ 'font-weight-medium': row.bold }">{{ row.value }}</span>
                              </div>
                            </template>
                          </div>
                        </v-tooltip>
                      </template>
                    </v-data-table>

                    <div class="d-flex justify-end mt-4" style="max-width: 720px">
                      <v-btn variant="text" size="small" color="primary" @click="$dialog.push('league-chart')">VIEW CHART</v-btn>
                    </div>
                  </template>
                  <p v-else class="text-body-2 text-medium-emphasis mt-4">
                    No teams yet.
                  </p>

                <template v-if="league.bracketSize >= 2 && league.squads.length > 0">
                  <p class="text-body-2 font-weight-medium text-medium-emphasis mb-2 mt-9">Knockout bracket</p>
                  <div class="bracket-scroll">
                    <bracket-viewer :rounds="rounds" :consolation="consolation" />
                  </div>
                </template>
                </v-card-text>
              </v-card>
            </v-tabs-window-item>

            <v-tabs-window-item value="matches">
              <v-card flat style="max-width: 550px;">
                <v-card-text style="padding-bottom: 125px;">
                  <div
                    v-if="sortedRaces.length > 0"
                    class="d-flex flex-column mt-4"
                    style="gap: 16px;"
                  >
                    <template v-for="matchRace in sortedRaces" :key="matchRace.id">
                      <RaceCard
                        v-if="matchRace.loaded"
                        :race="matchRace"
                        :data-test="'race-item-' + matchRace.id"
                        score-shadow="none"
                        @click="openRace(matchRace)"
                      />
                    </template>
                  </div>
                  <p v-else class="text-body-2 text-medium-emphasis mt-4">
                    No matches yet. They show up on match day.
                  </p>
                </v-card-text>
              </v-card>
            </v-tabs-window-item>

            <v-tabs-window-item value="schedule">
              <v-card flat style="max-width: 550px;">
                <v-card-text style="padding-bottom: 125px;">
                  <template v-if="league.schedule.errors.length > 0">
                    <v-alert
                      type="warning"
                      density="compact"
                      variant="tonal"
                      class="mb-2"
                    >
                      <div v-for="error in league.schedule.errors" :key="error" class="text-body-2">
                        {{ error }}
                      </div>
                    </v-alert>
                  </template>
                  <template v-else-if="league.schedule.events.length > 0">
                    <div v-for="week in scheduleByWeek" :key="week.week" class="mb-3">
                      <div class="text-body-2 font-weight-medium text-primary" :class="{ 'week-past': isWeekPast(week) }">
                        Week {{ week.week }}
                      </div>
                      <v-list density="compact" class="mb-1">
                        <v-list-item
                          v-for="race in week.races"
                          :key="race.isBlackout ? 'bo-' + race.date.getTime() : race.homeSquadId + '-' + race.awaySquadId"
                          density="compact"
                          lines="three"
                          class="schedule-item"
                        >
                          <template v-slot:prepend>
                            <v-icon>{{ race.isBlackout ? 'mdi-calendar-remove' : 'mdi-flag-checkered' }}</v-icon>
                          </template>
                          <template v-if="race.isBlackout">
                            <v-list-item-title class="text-body-2 font-weight-medium">No matches</v-list-item-title>
                            <v-list-item-subtitle class="text-body-2">
                              <span class="text-caption text-medium-emphasis">{{ formatRaceDate(race.date) }}</span>
                            </v-list-item-subtitle>
                          </template>
                          <template v-else>
                            <v-list-item-title class="schedule-matchup-title text-body-2 font-weight-medium">
                              {{ race.homeSquad.title }} vs. {{ race.awaySquad.title }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-body-2">
                              <template v-if="race.homeSquad?.homeLocation">
                                {{ race.homeSquad.homeLocation.name }} · Table {{ race.tableNumber }}
                                <br>
                                <span class="text-caption text-medium-emphasis">
                                  {{ formatRaceDate(race.date) }}
                                </span>
                              </template>
                              <template v-else>
                                {{ formatRaceDate(race.date) }}
                              </template>
                            </v-list-item-subtitle>
                          </template>
                        </v-list-item>
                        <v-list-item
                          v-if="week.byes.length > 0"
                          density="compact"
                          prepend-icon="mdi-calendar-blank-outline"
                        >
                          <v-list-item-title class="text-body-2 font-weight-medium">
                            {{ week.byes.map(bye => bye.title).join(", ") }}
                          </v-list-item-title>
                          <v-list-item-subtitle class="text-body-2 text-medium-emphasis">
                            Bye
                          </v-list-item-subtitle>
                        </v-list-item>
                      </v-list>
                    </div>
                    <div v-for="week in bracketScheduleWeeks" :key="'bracket-' + week.week" class="mb-3">
                      <div class="text-body-2 font-weight-medium text-primary" :class="{ 'week-past': isWeekPast(week) }">
                        Playoff Week {{ week.week }}
                      </div>
                      <v-list density="compact" class="mb-1">
                        <v-list-item
                          v-for="event in week.bracketEvents"
                          :key="event.bracketSlot"
                          density="compact"
                          lines="three"
                          class="schedule-item"
                          prepend-icon="mdi-trophy"
                        >
                          <v-list-item-title class="schedule-matchup-title text-body-2 font-weight-medium">
                            {{ bracketEventLabel(event) }}
                          </v-list-item-title>
                          <template v-if="!event.isBye">
                            <v-list-item-subtitle v-if="event.scheduled && event.scheduled.date" class="text-body-2">
                              <template v-if="event.location">
                                {{ event.location.name }} · Table {{ event.scheduled.tableNumber }}
                                <br>
                                <span class="text-caption text-medium-emphasis">
                                  {{ formatRaceDate(event.scheduled.date) }}
                                </span>
                              </template>
                              <template v-else>
                                {{ formatRaceDate(event.scheduled.date) }}
                              </template>
                            </v-list-item-subtitle>
                            <v-list-item-subtitle v-else class="text-body-2 text-medium-emphasis">
                              To be scheduled
                            </v-list-item-subtitle>
                          </template>
                        </v-list-item>
                      </v-list>
                    </div>
                  </template>
                  <p v-else class="text-body-2 text-medium-emphasis">
                    Need at least 2 teams to generate the schedule.
                  </p>

                  <template v-if="league.freeAgents.length > 0">
                    <p class="text-body-2 font-weight-medium text-medium-emphasis mb-2 mt-9">
                      Free agents / Subs
                    </p>
                    <v-list density="compact" class="pa-0">
                      <v-list-item
                        v-for="member in league.freeAgents"
                        :key="member.id"
                        density="compact"
                        class="px-0"
                        prepend-icon="mdi-account"
                      >
                        {{ member.name }}
                        <span class="text-medium-emphasis">
                          ({{ member.startOfLeagueDisplayRating }})
                        </span>
                        <template v-if="isCreator" v-slot:append>
                          <v-btn
                            icon
                            variant="text"
                            density="compact"
                            data-test="paid-toggle"
                            :color="member.paid ? 'success' : 'grey'"
                            @click="member.immediateUpdate({ paid: !member.paid })"
                          >
                            <v-icon>{{ member.paid ? 'mdi-check-circle' : 'mdi-cancel' }}</v-icon>
                          </v-btn>
                        </template>
                      </v-list-item>
                    </v-list>
                  </template>
                </v-card-text>
              </v-card>

              <v-card
                v-if="isCreator && league.schedule.events.length > 0"
                flat
                class="mt-4"
                style="max-width: 720px;"
              >
                <v-card-text>
                  <p class="text-body-2 font-weight-medium mb-1">
                    Schedule checks
                  </p>
                  <v-list density="compact" class="mt-1">
                    <v-list-item
                      v-for="check in scheduleChecks"
                      :key="check.key || check.label"
                      density="compact"
                    >
                      <template v-slot:prepend>
                        <v-icon
                          :icon="check.passed ? 'mdi-check-circle' : 'mdi-close-circle'"
                          :color="check.passed ? 'success' : 'error'"
                          size="small"
                        />
                      </template>
                      <v-list-item-title class="schedule-check-label text-body-2">{{ check.label }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                  <v-data-table
                    v-if="travelCheck"
                    :headers="travelHeaders"
                    :items="travelCheck.data"
                    item-value="squad.id"
                    density="compact"
                    items-per-page="-1"
                    hide-default-footer
                    class="mt-2"
                  >
                    <template #[`item.team`]="{ item }">
                      <div class="font-weight-medium">{{ item.squad.title }}</div>
                      <div v-if="item.squad.homeLocation" class="text-body-2 text-medium-emphasis">
                        {{ item.squad.homeLocation.name }}
                      </div>
                    </template>
                    <template #[`item.fairTarget`]="{ item }">
                      <span class="text-medium-emphasis">{{ formatHalf(item.fairTarget) }}</span>
                    </template>
                    <template #[`item.deviation`]="{ item }">
                      <span :class="Math.abs(item.deviation) > 0.5 ? 'text-error' : 'text-medium-emphasis'">
                        {{ formatSigned(item.deviation) }}
                      </span>
                    </template>
                  </v-data-table>
                  <v-data-table
                    v-if="bracketScheduleCheck && bracketScheduleCheck.data.length > 0"
                    :headers="bumpHeaders"
                    :items="bracketScheduleCheck.data"
                    item-value="slotId"
                    density="compact"
                    items-per-page="-1"
                    hide-default-footer
                    class="mt-2"
                  >
                    <template #[`item.slotId`]="{ item }">
                      <span class="text-error font-weight-medium">{{ item.slotId }}</span>
                    </template>
                    <template #[`item.expectedLocation`]="{ item }">
                      <span class="text-error">{{ item.expectedLocation }}</span>
                    </template>
                    <template #[`item.actualLocation`]="{ item }">
                      <span>{{ item.actualLocation }}</span>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-tabs-window-item>

            <v-tabs-window-item value="settings">
              <v-card flat style="max-width: 550px;">
                <v-card-text style="padding-bottom: 125px;">
                  <v-text-field
                    label="League name"
                    variant="underlined"
                    autocomplete="off"
                    spellcheck="false"
                    class="mt-4"
                    :model-value="league.name"
                    @update:model-value="league.update({ name: $event })"
                  />

                  <div>
                    <p class="text-body-2 font-weight-medium text-medium-emphasis mb-2 mt-12">
                      Schedule
                    </p>

                    <v-menu
                      v-model="startDateMenu"
                      :close-on-content-click="false"
                      min-width="auto"
                      transition="scale-transition"
                    >
                      <template v-slot:activator="{ props }">
                        <v-text-field
                          :model-value="league.startDateString"
                          label="Season start date"
                          variant="underlined"
                          autocomplete="off"
                          spellcheck="false"
                          prepend-icon="mdi-calendar"
                          readonly
                          class="mt-4"
                          v-bind="props"
                        />
                      </template>
                      <v-date-picker :model-value="league.startDateString" @update:model-value="updateStartDate">
                        <template v-slot:actions>
                          <v-spacer />
                          <v-btn
                            variant="flat"
                            color="primary"
                            @click="startDateMenu = false"
                          >
                            OK
                          </v-btn>
                        </template>
                      </v-date-picker>
                    </v-menu>

                    <v-menu
                      v-model="blackoutMenu"
                      :close-on-content-click="false"
                      min-width="auto"
                      transition="scale-transition"
                    >
                      <template v-slot:activator="{ props }">
                        <v-text-field
                          :model-value="blackoutDate"
                          label="Add a blackout date"
                          variant="underlined"
                          autocomplete="off"
                          spellcheck="false"
                          prepend-icon="mdi-calendar-remove"
                          readonly
                          class="mt-4"
                          v-bind="props"
                        />
                      </template>
                      <v-date-picker
                        :model-value="blackoutDate"
                        @update:model-value="blackoutDate = toInputDate($event)"
                      >
                        <template v-slot:actions>
                          <v-spacer />
                          <v-btn
                            variant="flat"
                            color="primary"
                            :disabled="!blackoutDate || league.blackoutDates.includes(blackoutDate)"
                            @click="addBlackoutDate"
                          >
                            OK
                          </v-btn>
                        </template>
                      </v-date-picker>
                    </v-menu>

                    <template v-if="league.blackoutDates.length > 0">
                      <div
                        v-for="date in league.blackoutDates"
                        :key="date"
                        class="mb-1 d-flex align-center"
                      >
                        <v-icon icon="mdi-calendar-remove" size="small" class="mr-2" />
                        <span class="text-body-2">{{ formatBlackoutDate(date) }}</span>
                        <v-btn
                          icon
                          variant="text"
                          size="small"
                          class="ml-auto"
                          @click="removeBlackoutDate(date)"
                        >
                          <v-icon icon="mdi-delete" size="small" />
                        </v-btn>
                      </div>
                    </template>
                  </div>

                  <p class="text-body-2 font-weight-medium text-medium-emphasis mb-2 mt-10">
                    Locations
                  </p>

                  <template v-if="league.locations.length > 0">
                    <card-shell
                      v-for="location in league.locations"
                      :key="location.id"
                      class="mb-3 mt-4"
                      :elevation="2"
                      clickable
                      @click="openLocationDialog(location)"
                    >
                      <template #actions>
                        <v-btn
                          icon
                          variant="text"
                          size="small"
                          @click.stop="location.destroy()"
                        >
                          <v-icon icon="mdi-delete" size="small" />
                        </v-btn>
                      </template>
                      <div class="d-flex align-center text-body-1">
                        <v-icon icon="mdi-map-marker" size="small" class="mr-2" />
                        <span class="font-weight-medium">{{ location.name }}</span>
                      </div>
                      <div class="pl-6">
                        <span class="text-body-2 text-medium-emphasis">{{ location.numberOfTables }} {{ location.numberOfTables === 1 ? 'table' : 'tables' }}</span>
                        <div v-if="location.slots.length > 0" class="text-body-2 text-medium-emphasis">
                          {{ location.slots.map(slot => slotLabels(slot)).join(', ') }}
                        </div>
                      </div>
                    </card-shell>
                  </template>
                  <v-btn
                    class="mt-2"
                    variant="outlined"
                    prepend-icon="mdi-plus"
                    @click="createLocation"
                  >
                    Add Location
                  </v-btn>

                  <p class="text-body-2 font-weight-medium text-medium-emphasis mb-2 mt-16">
                    Structure and points
                  </p>

                  <div class="d-flex mt-8">
                    <v-number-input
                      label="Times each pair plays"
                      variant="underlined"
                      control-variant="stacked"
                      class="mr-4"
                      :model-value="league.timesEachPairPlays"
                      @update:model-value="league.update({ timesEachPairPlays: Number($event) })"
                      :min="1"
                      :disabled="league.hasScoredGame"
                    />

                    <v-number-input
                      label="Points to win a race"
                      variant="underlined"
                      control-variant="stacked"
                      :model-value="league.pointsToWin"
                      @update:model-value="league.update({ pointsToWin: Number($event) })"
                      :min="1"
                      :disabled="league.hasScoredGame"
                    />
                  </div>

                  <div class="d-flex">
                    <v-number-input
                      label="Bracket size"
                      variant="underlined"
                      control-variant="stacked"
                      class="mt-4 mr-4"
                      :model-value="league.bracketSize"
                      @update:model-value="league.update({ bracketSize: Number($event) })"
                      :min="0"
                      :disabled="league.hasScoredGame"
                    />

                    <v-number-input
                      label="Points per game"
                      variant="underlined"
                      control-variant="stacked"
                      class="mt-4"
                      :model-value="league.pointsPerGame"
                      @update:model-value="league.update({ pointsPerGame: Number($event) })"
                      :min="1"
                      :disabled="league.hasScoredGame"
                    />
                  </div>
                  <p v-if="league.hasScoredGame" class="text-body-2 text-medium-emphasis mt-2">
                    These are locked once a game has been scored, because changing them would require regenerating races and erase results.
                  </p>

                  <div class="team-management">
                  <p class="text-body-2 font-weight-medium text-medium-emphasis mb-2 mt-12">
                    Teams
                  </p>

                  <v-list density="compact" class="pa-0 settings-teams-list">
                    <template v-for="(squad, index) in league.squads" :key="squad.id">
                    <v-list-item
                      density="compact"
                      lines="three"
                      class="px-0"
                    >
                      <template v-slot:prepend>
                        <v-icon :icon="squadMembersIcon(squad)" />
                      </template>
                      <v-list-item-title class="text-body-2 font-weight-medium">{{ squad.title }}</v-list-item-title>
                      <v-list-item-subtitle class="text-body-2">
                        <template v-if="squad.homeLocation">
                          {{ squad.homeLocation.name }}
                          <br>
                        </template>
                        <template v-if="squad.membersSortedByStartOfLeagueOrdinal.length > 0">
                          {{ squad.membersSortedByStartOfLeagueOrdinal.map(member => member.name).join(", ") }}
                        </template>
                      </v-list-item-subtitle>
                      <template v-slot:append>
                        <v-menu location="left" :close-on-content-click="false">
                          <template v-slot:activator="{ props }">
                            <v-btn icon variant="text" size="small" density="compact" v-bind="props">
                              <v-icon icon="mdi-dots-vertical" size="small" />
                            </v-btn>
                          </template>
                          <v-list density="compact" class="dialog__menu">
                            <v-list-item @click="openSquadDialog(squad)">
                              <template v-slot:prepend>
                                <v-icon icon="mdi-pencil" size="small" />
                              </template>
                              <v-list-item-title>Edit team</v-list-item-title>
                            </v-list-item>
                            <v-list-item :disabled="league.hasScoredGame" @click="squad.destroy()">
                              <template v-slot:prepend>
                                <v-icon icon="mdi-delete" size="small" />
                              </template>
                              <v-list-item-title>Remove team</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </template>
                    </v-list-item>
                    <v-divider v-if="index < league.squads.length - 1" inset />
                    </template>
                  </v-list>

                  <v-btn
                    class="mt-6"
                    variant="outlined"
                    prepend-icon="mdi-plus"
                    :disabled="league.hasScoredGame || league.locations.length === 0"
                    @click="createSquad"
                  >
                    Add Team
                  </v-btn>
                  <p v-if="league.locations.length === 0" class="text-body-2 text-medium-emphasis mt-2">
                    Add a location before adding teams.
                  </p>
                  <p v-if="league.hasScoredGame" class="text-body-2 text-medium-emphasis mt-2">
                    Teams cannot be added or removed once a game has been scored.
                  </p>
                  </div>
                </v-card-text>
              </v-card>
            </v-tabs-window-item>
          </v-tabs-window>
        </template>

        <div v-else class="d-flex align-center justify-center" style="min-height: calc(100vh - var(--v-layout-top) - 48px);">
          <v-progress-circular
            v-if="!showNotFound"
            indeterminate
            color="primary"
            size="36"
            width="3"
          />
          <p v-else class="text-body-1">
            League not found.
          </p>
        </div>
      </template>
    </v-container>
  </standard-layout>
</template>

<script>
import StandardLayout from "@/components/standard-layout.vue";
import RaceCard from "@/components/race-card.vue";
import BracketViewer from "@/components/bracket-viewer.vue";
import CardShell from "@/components/card-shell.vue";
import PlayerNameCard from "@/components/player-name-card.vue";
import { db, dbFns } from "@/db";
import { createSession } from "@/models/session";
import { checkLeagueSchedule, checkBracketSchedule } from "@/utils/schedule-checks";
import { dateToInputString, weekdayNames, minutesToClock } from "@/utils/time";
import { raceDocumentsForSchedule, bracketEventLabel as formatBracketEventLabel } from "@/utils/race-utils";
import { bracketRounds, bracketConsolation } from "@/utils/league-bracket-display";
import { leaguePayouts } from "@/utils/league-payout";
import { nextPowerOf2 } from "@/utils/league-bracket";
import { groupScheduleByWeek, groupBracketByWeek, renumberWeekGroups } from "@/utils/league-schedule-views";
import { startConfetti, stopConfetti } from "@/utils/confetti";
import { POINTS_WIN, POINTS_NEAR_LOSS, NEAR_LOSS_MARGIN } from "@/utils/league-points";
import { PLACE_COLORS } from "@/utils/league-constants";

export default {
  components: {
    StandardLayout,
    RaceCard,
    BracketViewer,
    CardShell,
    PlayerNameCard
  },
  data () {
    return {
      league: null,
      leagueCreator: null,
      localMemberName: "",
      tab: null,
      startDateMenu: false,
      blackoutMenu: false,
      blackoutDate: "",
      autoGenerateTimer: null,
      showNotFound: false,
      notFoundTimeoutId: null
    };
  },
  created () {
    this.tab = this.$route.query.tab || "teams";
    this.notFoundTimeoutId = setTimeout(() => {
      this.showNotFound = true;
    }, 15000);
    this.findLeagueCreator().then((creator) => {
      this.leagueCreator = creator;
      this.loadLeague();
    });
  },
  beforeUnmount () {
    stopConfetti();
    if (this.notFoundTimeoutId) {
      clearTimeout(this.notFoundTimeoutId);
    }
    if (this.league) {
      this.$store.league = null;
      this.league.unsubscribe();
    }
    if (this.autoGenerateTimer) {
      clearTimeout(this.autoGenerateTimer);
      this.autoGenerateTimer = null;
    }
  },
  watch: {
    tab (tab) {
      if (tab === "teams") {
        this.$router.removeQueryParam("tab");
      } else {
        this.$router.updateQueryParams({ tab });
      }
    },
    "league.finished" (val) {
      if (val) {
        startConfetti();
      } else {
        stopConfetti();
      }
    },
    "league.schedule": {
      handler () {
        this.scheduleAutoGenerate();
      }
    },
    shouldAutoGenerate () {
      this.scheduleAutoGenerate();
    }
  },
  computed: {
    hasMemberName () {
      return !!(this.$store.user.playerName && this.$store.user.playerName.trim());
    },
    isCreator () {
      return this.leagueCreator && this.$store.user && this.leagueCreator === this.$store.user;
    },
    showPayout () {
      return (import.meta.env.VITE_NODE_ENV === "development" && !window.Cypress) || this.leagueCreator?.role === "owner";
    },
    sortedRaces () {
      const todayMs = this.scheduleDayMs(new Date());
      return this.league.races
        .filter(race => race.decided || (race.date && this.scheduleDayMs(race.date) <= todayMs))
        .sort((a, b) => {
          const decidedA = a.decided ? 1 : 0;
          const decidedB = b.decided ? 1 : 0;
          if (decidedA !== decidedB) return decidedA - decidedB;
          const dateDiff = this.scheduleDayMs(b.date) - this.scheduleDayMs(a.date);
          if (dateDiff !== 0) return dateDiff;
          const timeA = this.scheduleSeconds(a.date);
          const timeB = this.scheduleSeconds(b.date);
          if (timeA !== timeB) return timeA - timeB;
          const locationDiff = (a.location?.name || "").localeCompare(b.location?.name || "");
          if (locationDiff !== 0) return locationDiff;
          return (a.tableNumber || 0) - (b.tableNumber || 0);
        });
    },
    canGenerateRaces () {
      return this.league.schedule.errors.length === 0 && this.league.schedule.events.length > 0;
    },
    shouldAutoGenerate () {
      return !!this.league &&
        this.isCreator &&
        this.league.racesNeedRegeneration &&
        !this.league.hasScoredGame &&
        this.canGenerateRaces;
    },
    squadHeaders () {
      const hasBracket = this.league.bracketSize >= 2;
      const headers = [
        { title: hasBracket ? "Seed" : "#", key: "place", sortable: false, width: "48px" },
        { title: "Team", key: "team", sortable: false },
        { title: "W/L", key: "record", sortable: false, width: "24px", align: "center" },
        { title: "Pts", key: "points", sortable: false, width: "24px", align: "center" },
        { title: "Goals", key: "goals", sortable: false, width: "24px", align: "center" }
      ];
      if (!hasBracket && this.leaguePayoutInfo && this.showPayout) {
        headers.push({ title: "$", key: "payout", sortable: false, width: "40px", align: "center" });
      }
      return headers;
    },
    scheduleWeekGroups () {
      return groupScheduleByWeek({
        events: this.league.schedule.events,
        squads: this.league.squads
      });
    },
    scheduleWeekOffset () {
      const groups = this.scheduleWeekGroups;
      return groups.length > 0 ? groups[0].week : 1;
    },
    scheduleByWeek () {
      return renumberWeekGroups(this.scheduleWeekGroups, this.scheduleWeekOffset);
    },
    bracketScheduleWeekGroups () {
      return groupBracketByWeek({
        events: this.league.schedule.events,
        slotSquads: this.league.bracketSquads,
        schedule: this.league.bracketSchedule,
        locations: this.league.locations
      });
    },
    bracketScheduleWeeks () {
      return renumberWeekGroups(this.bracketScheduleWeekGroups, this.scheduleWeekOffset);
    },
    scheduleChecks () {
      const checks = checkLeagueSchedule(this.league, this.league.schedule.events);
      if (this.league.bracketSize >= 2) {
        checks.push(checkBracketSchedule(this.league));
      }
      return checks;
    },
    travelCheck () {
      return this.scheduleChecks.find(check => check.key === "travel");
    },
    bracketScheduleCheck () {
      return this.scheduleChecks.find(check => check.key === "bracket");
    },
    travelHeaders () {
      return [
        { key: "team", title: "Team" },
        { key: "travel", title: "Travel" },
        { key: "fairTarget", title: "Fair target" },
        { key: "deviation", title: "vs fair" }
      ];
    },
    bumpHeaders () {
      return [
        { key: "slotId", title: "Game" },
        { key: "expectedLocation", title: "Home venue" },
        { key: "actualLocation", title: "Scheduled at" }
      ];
    },
    rounds () {
      return bracketRounds(this.league);
    },
    consolation () {
      return bracketConsolation(this.league);
    },
    bracketHasByes () {
      const teams = Math.min(this.league.bracketSize, this.league.squads.length);
      return teams >= 2 && nextPowerOf2(teams) > teams;
    },
    leaguePayoutInfo () {
      return leaguePayouts({
        squadCount: this.league.squads.length,
        timesEachPairPlays: this.league.timesEachPairPlays,
        bracketRoundCount: this.league.bracketRoundCount,
        bracketHasByes: this.bracketHasByes
      });
    },
    payoutTooltipRows () {
      const info = this.leaguePayoutInfo;
      if (!info) return [];
      return [
        { label: `Entry fees ($10 x ${info.races} weeks x ${info.players} players)`, value: `$${info.entryFees}` },
        { label: `To AFC ($20 x ${info.races} weeks)`, value: `− $${info.afcFee}` },
        { label: "Total payout", value: `$${info.pool}`, bold: true }
      ];
    },
    finalStandingsList () {
      const payouts = this.leaguePayoutInfo?.byPlace || {};
      return this.league.finalStandings.map((item, index) => {
        const position = index + 1;
        return {
          ...item,
          position,
          payout: payouts[position] != null ? payouts[position] : null
        };
      });
    },
    rankedSquadsWithPayout () {
      const payouts = this.leaguePayoutInfo?.byPlace || {};
      return this.league.rankedSquads.map(item => ({
        ...item,
        payout: item.place != null && payouts[item.place] != null ? payouts[item.place] : null
      }));
    },
    placeColors () {
      return PLACE_COLORS;
    },
    pointsTooltip () {
      return `${POINTS_WIN} points for a win. ${POINTS_NEAR_LOSS} point for a ${this.league.pointsToWin - NEAR_LOSS_MARGIN}-${this.league.pointsToWin} loss.`;
    }
  },
  methods: {
    placeMedal (item) {
      return item.revealed === true && item.place >= 1 && item.place <= 3;
    },
    findLeagueCreator () {
      const docRef = dbFns.doc(db, "leagues", this.$route.params.id);
      return dbFns.getDoc(docRef).then((docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          if (data.userId !== this.$store.user.id) {
            const session = createSession(this.$store);
            return session.findUserById(data.userId);
          }
        }
        return this.$store.user;
      });
    },
    loadLeague () {
      const docRef = dbFns.doc(db, "leagues", this.$route.params.id);
      const LeagueModel = this.leagueCreator.getModel("league");
      const league = new LeagueModel({
        propsData: {
          docRef,
          user: this.leagueCreator,
          parent: this.leagueCreator,
          startSnapshot: true,
          docType: "league"
        }
      });
      Promise.all([league.promiseOnSnapshot, ...league.prereqPromises]).then(() => {
        clearTimeout(this.notFoundTimeoutId);
        this.league = league;
        this.$store.league = league;
        this.addVisitorsAutomatically();
        this.handleConfetti();
      });
    },
    bracketEventLabel (event) {
      return formatBracketEventLabel(
        event,
        this.league.bracketSquads[event.bracketSlot],
        this.league.squads,
        this.league.bracketRoundCount
      );
    },
    addVisitorsAutomatically () {
      if (this.$store.joinMemberPromise) {
        return this.$store.joinMemberPromise;
      }
      this.$store.joinMemberPromise = this.isMemberAlready().then((exists) => {
        if (!exists) {
          return this.addMeToLeague().then(() => {
            this.$store.pushSnackbarMessageProps({ message: "You've been added to the league." });
          });
        }
      }).finally(() => {
        this.$store.joinMemberPromise = null;
      });
      return this.$store.joinMemberPromise;
    },
    createSquad () {
      this.league.createSquad({
        name: "Untitled",
        locationId: this.league.locations[0].id,
        memberIds: []
      }).then((id) => this.findSquad(id)).then((squad) => {
        this.$dialog.push("squads", squad.id);
      });
    },
    findSquad (id) {
      const existing = this.league.squads.find(s => s.id === id);
      if (existing) return Promise.resolve(existing);
      return new Promise((resolve) => {
        const unwatch = this.$watch(() => this.league.squads, (squads) => {
          const squad = squads.find(s => s.id === id);
          if (squad) {
            unwatch();
            resolve(squad);
          }
        });
      });
    },
    openSquadDialog (squad) {
      this.$dialog.push("squads", squad.id);
    },
    squadMembersIcon (squad) {
      switch (squad.memberIds.length) {
      case 0:
        return "mdi-account-alert-outline";
      case 1:
        return "mdi-account-minus";
      case 2:
        return "mdi-account-multiple-minus";
      default:
        return "mdi-account-group";
      }
    },
    createLocation () {
      this.league.createLocation({ name: "Untitled", numberOfTables: 1, slots: [{ dayOfWeek: 0, time: 1140 }] }).then((id) => this.findLocation(id)).then((location) => {
        this.$dialog.push("locations", location.id);
      });
    },
    findLocation (id) {
      const existing = this.league.locations.find(l => l.id === id);
      if (existing) return Promise.resolve(existing);
      return new Promise((resolve) => {
        const unwatch = this.$watch(() => this.league.locations, (locations) => {
          const location = locations.find(l => l.id === id);
          if (location) {
            unwatch();
            resolve(location);
          }
        });
      });
    },
    openLocationDialog (location) {
      this.$dialog.push("locations", location.id);
    },
    findRace (id) {
      const existing = this.league.races.find(r => r.id === id);
      if (existing) return Promise.resolve(existing);
      return new Promise((resolve) => {
        const unwatch = this.$watch(() => this.league.races, (races) => {
          const race = races.find(r => r.id === id);
          if (race) {
            unwatch();
            resolve(race);
          }
        });
      });
    },
    scheduleAutoGenerate () {
      this.league.races;
      const racesReady = this.league.promiseRaces || Promise.resolve();
      racesReady.then(() => {
        if (this.autoGenerateTimer) {
          clearTimeout(this.autoGenerateTimer);
        }
        this.autoGenerateTimer = setTimeout(() => {
          this.autoGenerateTimer = null;
          if (this.shouldAutoGenerate) {
            this.autoGenerateRaces();
          }
        }, 4000);
      });
    },
    autoGenerateRaces () {
      if (!this.generateRacesPromise) {
        this.generateRacesPromise = Promise.resolve();
      }
      this.generateRacesPromise = this.generateRacesPromise
        .then(() => this.shouldAutoGenerate ? this.generateRaces() : undefined);
    },
    generateRaces () {
      const destroyExisting = this.league.races.length > 0
        ? Promise.all(this.league.races.map(race => race.destroy()))
        : Promise.resolve();
      return destroyExisting
        .then(() => Promise.all(
          raceDocumentsForSchedule(this.league.schedule.events).map(async (payload) => {
            const raceId = await this.league.createRace({
              ...payload
            });
            const race = await this.findRace(raceId);
            await race.createGame({ scores: {}, active: true });
          })
        ))
        .then(async () => {
          await Promise.all(
            this.league.members.map(async m => {
              await m.promiseUserModel;
              m.userModel.scorecard;
              await m.userModel.promiseScorecard;
            })
          );
          await Promise.all(
            this.league.members.map(m =>
              m.immediateUpdate({
                startOfLeagueMu: m.userModel.scorecard.mu,
                startOfLeagueSigma: m.userModel.scorecard.sigma
              })
            )
          );
        });
    },
    openRace (race) {
      this.$dialog.push("races", race.id);
    },
    handleConfetti () {
      if (this.league?.finished) {
        startConfetti();
      }
    },
    isMemberAlready () {
      const { collection, query, where, getDocs } = dbFns;
      const q = query(
        collection(db, "members"),
        where("leagueId", "==", this.league.id),
        where("memberUserId", "==", this.$store.user.id)
      );
      return getDocs(q).then((snapshot) => !snapshot.empty);
    },
    addMeToLeague () {
      if (!this.leagueCreator || !this.leagueCreator.id) return Promise.resolve();
      this.$store.user.scorecard;
      return this.$store.user.promiseScorecard.then((scorecard) => {
        return this.league.createMember({
          userId: this.leagueCreator.id,
          memberUserId: this.$store.user.id,
          startOfLeagueMu: scorecard.mu,
          startOfLeagueSigma: scorecard.sigma
        });
      });
    },
    updateStartDate (value) {
      if (!value) return;
      let d;
      if (typeof value === "string") {
        d = new Date(`${value}T00:00:00`);
      } else if (typeof value.toDate === "function") {
        d = value.toDate();
      } else {
        d = new Date(value);
      }
      d.setHours(0, 0, 0, 0);
      this.league.update({ startDate: d });
    },
    toInputDate (value) {
      return dateToInputString(value);
    },
    addBlackoutDate () {
      const value = dateToInputString(this.blackoutDate);
      if (!value || this.league.blackoutDates.includes(value)) return;
      this.league.update({ blackoutDates: [...this.league.blackoutDates, value] });
      this.blackoutDate = "";
      this.blackoutMenu = false;
    },
    removeBlackoutDate (date) {
      this.league.update({ blackoutDates: this.league.blackoutDates.filter(d => d !== date) });
    },
    formatBlackoutDate (date) {
      const iso = dateToInputString(date);
      if (!iso) return "";
      const d = new Date(`${iso}T00:00:00`);
      return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    },
    slotLabels (slot) {
      return `${weekdayNames[slot.dayOfWeek]} · ${minutesToClock(slot.time)}`;
    },
    scheduleSeconds (date) {
      if (!date) return 0;
      const d = date.toDate ? date.toDate() : new Date(date);
      return d.getHours() * 3600 + d.getMinutes() * 60 + d.getSeconds();
    },
    scheduleDayMs (date) {
      if (!date) return 0;
      const d = date.toDate ? date.toDate() : new Date(date);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    },
    formatRaceDate (date) {
      if (!date) return "";
      const d = date.toDate ? date.toDate() : new Date(date);
      const dateLabel = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
      const timeLabel = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
      return `${dateLabel} · ${timeLabel}`;
    },
    isWeekPast (week) {
      const dates = [];
      (week.races || []).forEach(race => { if (race.date) dates.push(race.date); });
      (week.bracketEvents || []).forEach(event => { if (event.scheduled?.date) dates.push(event.scheduled.date); });
      if (dates.length === 0) return false;
      const last = dates
        .map(date => (date.toDate ? date.toDate() : new Date(date)))
        .sort((a, b) => a - b)
        .pop();
      const nextSunday = new Date(last);
      const daysToNextSunday = last.getDay() === 0 ? 7 : 7 - last.getDay();
      nextSunday.setDate(nextSunday.getDate() + daysToNextSunday);
      nextSunday.setHours(0, 0, 0, 0);
      return Date.now() >= nextSunday.getTime();
    },
    formatHalf (value) {
      return Number.isInteger(value) ? String(value) : value.toFixed(1);
    },
    formatSigned (value) {
      const rounded = Math.round(value * 10) / 10;
      const sign = rounded > 0 ? "+" : "";
      return `${sign}${rounded}`;
    }
  }
};
</script>

<style lang="css" scoped>
.tournament-tabs {
  position: sticky;
  top: var(--v-layout-top);
  z-index: 2;
  background-color: white;
  padding-top: 16px;
  margin-top: -16px;
}

.schedule-check-label {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}

.schedule-matchup-title {
  white-space: normal;
  text-overflow: clip;
  overflow: visible;
}

.schedule-item :deep(.v-list-item__prepend) {
  align-self: center;
}

.week-past {
  text-decoration: line-through;
}

.bracket-scroll {
  overflow-x: auto;
  padding-bottom: 8px;
}

.teams-table :deep(.v-data-table__td),
.teams-table :deep(.v-data-table__th) {
  padding: 0 9px;
}

.settings-teams-list :deep(.v-list-item__prepend) {
  align-self: center;
}

.settings-teams-list :deep(.v-list-item) {
  padding-left: 0;
}

.settings-teams-list :deep(.v-divider--inset) {
  margin-inline-start: 36px;
  max-width: calc(100% - 36px);
}

.final-standings-list :deep(.v-list-item__prepend) {
  align-self: center;
}

.final-standings-list :deep(.v-list-item) {
  padding-left: 0;
}

.final-standings-medal-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  margin-right: 16px;
}

.final-standings-dollar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 8px;
  border: 2px solid rgba(var(--v-theme-on-surface), 0.6);
  border-radius: 50%;
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
}

.final-standings-dollar-symbol {
  transform: translate(0.25px, 1px);
  font-weight: 700;
}

.payout-tooltip {
  max-width: 320px;
}
</style>
